// SPEC-AUTH-LOCK-S4: WebAuthn client helpers (@simplewebauthn/browser ラッパー)
// D-993: Face ID でログイン(使うを選んだ人だけ)。登録 / 解除 / ログイン と「この端末は ON」の印。
//   ★@simplewebauthn/browser@10 の startRegistration / startAuthentication は options を **そのまま** 第 1 引数に取る
//     (v11 の { optionsJSON } 形ではない)。旧コードは { optionsJSON } を渡していて challenge が undefined になり、
//     登録が端末側で必ず失敗していた。

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

// WKWebView / 未対応端末検出: navigator.credentials.create が存在しなければ false
export function isWebAuthnSupported() {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator?.credentials?.create === 'function' &&
    typeof navigator?.credentials?.get === 'function'
  )
}

// userAgent から端末ラベルを推定 (ユーザー入力なし)
function guessDeviceLabel() {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  return 'このデバイス'
}

// ─── この端末の印(staff_id → credential_id)────────────────────────
// ★端末ごとの ON/OFF。サーバには「どの端末か」が無いので、ON にした端末だけが印を持つ。
//   印が無い人には Face ID ボタンを出さない(=既定は OFF、誰にも強制しない)。
// ★キーはここ 1 か所(D-616)。
export const FACE_ID_DEVICE_KEY = 'clawops_faceid_v1'

function readMap() {
  try {
    const raw = localStorage.getItem(FACE_ID_DEVICE_KEY)
    const v = raw ? JSON.parse(raw) : {}
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
  } catch {
    return {}
  }
}

function writeMap(map) {
  try {
    if (Object.keys(map).length === 0) localStorage.removeItem(FACE_ID_DEVICE_KEY)
    else localStorage.setItem(FACE_ID_DEVICE_KEY, JSON.stringify(map))
  } catch { /* private mode 等: 印が残らないだけ(PIN は使える) */ }
}

export function getDeviceCredentialId(staffId) {
  if (!staffId) return null
  const id = readMap()[staffId]
  return typeof id === 'string' && id ? id : null
}

export function setDeviceCredentialId(staffId, credentialId) {
  if (!staffId || !credentialId) return
  writeMap({ ...readMap(), [staffId]: credentialId })
}

export function clearDeviceCredentialId(staffId) {
  const map = readMap()
  if (!(staffId in map)) return
  delete map[staffId]
  writeMap(map)
}

/** この端末でこのスタッフが Face ID ログインを ON にしているか(ボタンを出す条件)。 */
export function isFaceIdEnabledOnDevice(staffId) {
  return isWebAuthnSupported() && !!getDeviceCredentialId(staffId)
}

/** D-993b: この端末で Face ID ログインを ON にしている staff_id の一覧(WebAuthn が無い端末では空)。 */
export function listFaceIdEnabledStaffIds() {
  if (!isWebAuthnSupported()) return []
  return Object.entries(readMap())
    .filter(([id, cred]) => id && typeof cred === 'string' && cred)
    .map(([id]) => id)
}

// JWT の app_metadata.staff_id(表示用の読み取りだけ。権限判定には使わない)
function staffIdFromJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - b64.length % 4) % 4)))
    return payload?.app_metadata?.staff_id ?? null
  } catch {
    return null
  }
}

async function postJson(fn, body, accessToken) {
  const headers = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
    method: 'POST', headers, body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok, status: res.status, data }
}

// passkey 登録: begin → startRegistration → finish → staff_credentials INSERT → この端末に印
export async function registerPasskey(accessToken, staffId = staffIdFromJwt(accessToken)) {
  // @simplewebauthn/browser は使用時のみロード (コード分割)
  const { startRegistration } = await import('@simplewebauthn/browser')

  // Step 1: begin - challenge + options 取得
  const begin = await postJson('register-passkey', { action: 'begin' }, accessToken)
  if (!begin.ok) throw new Error(begin.data.error || 'begin failed')
  const { challenge_token, options } = begin.data

  // Step 2: navigator.credentials.create (Face ID / Touch ID ダイアログ)
  const registrationResponse = await startRegistration(options)

  // Step 3: finish - 検証 + INSERT
  const finish = await postJson('register-passkey', {
    action: 'finish',
    registration_response: registrationResponse,
    challenge_token,
    device_label: guessDeviceLabel(),
  }, accessToken)
  if (!finish.ok) throw new Error(finish.data.error || 'finish failed')

  const credentialId = finish.data.credential_id || registrationResponse?.id
  setDeviceCredentialId(staffId, credentialId)
  return { ok: true, credential_id: credentialId }
}

// トグル OFF: サーバの行を消して、この端末の印を外す。
//   ★サーバで消せなくても印は外す(以後この端末では Face ID ボタンが出ない=OFF の約束は守る)。
export async function removePasskey(accessToken, staffId = staffIdFromJwt(accessToken)) {
  const credentialId = getDeviceCredentialId(staffId)
  clearDeviceCredentialId(staffId)
  if (!credentialId) return { ok: true }
  let serverOk = false
  try {
    const res = await postJson('register-passkey', { action: 'remove', credential_id: credentialId }, accessToken)
    serverOk = res.ok
  } catch { /* 通信失敗: 印は外した */ }
  // 端末の「パスワード」アプリからも消えるよう、対応ブラウザ(Signal API)にだけ知らせる
  try {
    if (typeof window.PublicKeyCredential?.signalUnknownCredential === 'function') {
      await window.PublicKeyCredential.signalUnknownCredential({ rpId: window.location.hostname, credentialId })
    }
  } catch { /* 未対応・失敗は無視 */ }
  return { ok: true, serverOk }
}

// D-993b: begin(challenge)を先に取っておける期間。サーバの challenge_token は 2 分で切れるので余裕を見て 90 秒。
export const PREPARED_PASSKEY_TTL_MS = 90_000

/**
 * D-993b: begin だけ先に済ませる(ボタンを押してから get() までに通信を挟まないため)。
 *   ★iOS Safari は操作なしの credentials.get() をページごとに 1 回しか許さず、2 回目以降はタップの直後でないと拒む。
 *     タップ → begin の通信待ち → get() だとタップの判定が切れることがあるので、challenge を先に持っておく。
 *   投げない。失敗は null(押したときに今まで通り begin から行う)。
 */
export async function preparePasskeyLogin(staff) {
  if (!staff?.staff_id || !isWebAuthnSupported()) return null
  try {
    await import('@simplewebauthn/browser') // 押したときの import 待ちも無くしておく
    const begin = await postJson('verify-passkey', { action: 'begin', staff_id: staff.staff_id })
    if (!begin.ok || !begin.data?.options?.challenge || !begin.data?.challenge_token) return null
    return { staffId: staff.staff_id, challenge_token: begin.data.challenge_token, options: begin.data.options, at: Date.now() }
  } catch {
    return null
  }
}

function isFreshPrepared(prepared, staffId) {
  return !!prepared && prepared.staffId === staffId && !!prepared.options?.challenge && !!prepared.challenge_token &&
    Date.now() - prepared.at >= 0 && Date.now() - prepared.at < PREPARED_PASSKEY_TTL_MS
}

/**
 * Face ID でログイン。PIN ログイン(verifyPin)と同じ形 { ok, session } を返す。
 * ★失敗・キャンセル・未対応はすべて { ok:false }(呼び出し側は PIN へ落とす)。投げない。
 * D-993b: prepared(preparePasskeyLogin の戻り)を渡すと、新しければ begin を飛ばして get() を直ぐ呼ぶ。
 * @returns {Promise<{ok: true, session: object} | {ok: false, reason: 'cancelled'|'failed'|'unsupported'}>}
 */
export async function loginWithPasskey(staff, prepared = null) {
  if (!staff?.staff_id || !isWebAuthnSupported()) return { ok: false, reason: 'unsupported' }
  try {
    const { startAuthentication } = await import('@simplewebauthn/browser')
    let challenge_token, options
    if (isFreshPrepared(prepared, staff.staff_id)) {
      ({ challenge_token, options } = prepared)
    } else {
      const begin = await postJson('verify-passkey', { action: 'begin', staff_id: staff.staff_id })
      if (!begin.ok) return { ok: false, reason: 'failed' }
      ;({ challenge_token, options } = begin.data)
    }
    if (!options?.challenge) return { ok: false, reason: 'failed' }
    // D-993 gate_3 #2: サーバは登録の有無を漏らさないよう allowCredentials を常に空で返す(discoverable)。
    //   この端末の印があれば、その credential だけを自分で指定する(共有端末で他人の passkey を選ばせない)。
    //   印が無ければ空のまま = iOS が rpID の passkey を出す。
    const markId = getDeviceCredentialId(staff.staff_id)
    const authOptions = {
      ...options,
      allowCredentials: markId ? [{ id: markId, type: 'public-key' }] : [],
    }
    let assertion
    try {
      assertion = await startAuthentication(authOptions)
    } catch (e) {
      return { ok: false, reason: e?.name === 'NotAllowedError' ? 'cancelled' : 'failed' }
    }
    const finish = await postJson('verify-passkey', {
      action: 'finish', staff_id: staff.staff_id, challenge_token, assertion,
    })
    if (finish.ok && finish.data.session?.access_token) return { ok: true, session: finish.data.session }
    return { ok: false, reason: 'failed' }
  } catch {
    return { ok: false, reason: 'failed' }
  }
}
